import {
  EligibilityStatusCodes,
  Error as CwbError,
  GetUserAliasResponse400,
  GetUserProgressResponse,
} from "@amzn/client-workbench-api-model";
import axios, { AxiosError } from "axios";
import dayjs, { Dayjs } from "dayjs";
import { useContext, useEffect, useState } from "react";
import { UseQueryResult } from "react-query";

import { BLOCKING_ERROR_TYPE } from "@/apis/constants";
import { VerificationStatus } from "@/apis/IdentityCheckHelper";
import { Logger } from "@/apis/Logger";
import { ChatSupportContextReason } from "@/components/GetSupport/ChatWithITSupport/constants";
import { isDayOneForbidden } from "@/components/IdentityCheck/helper/rivHelper";
import { trackStructEventOnceAppSession } from "@/helpers/trackStructEventOnceAppSession";
import { useAmazonAlias } from "@/hooks/useAmazonAlias";
import { useUserProgress } from "@/hooks/useUserProgress";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";
import { Day1EventAction, Day1EventCategory } from "@/types/snowplow-events";

/**
 * Check if the current date is earlier than the NH's actual start date.
 */
const isEarlyAccess = (
  userProgress: GetUserProgressResponse
): {
  isEarlyAccessForbidden: boolean;
  candidateLocalStartDate?: Dayjs;
} => {
  if (userProgress.candidateStartDate) {
    const candidateLocalStartDate = dayjs.tz(
      userProgress.candidateStartDate,
      dayjs.tz.guess() // Get the user current timezone
    );
    const isEarlyThanStartDate = dayjs().isBefore(candidateLocalStartDate);

    return {
      isEarlyAccessForbidden:
        isDayOneForbidden(userProgress.verificationStatus) &&
        isEarlyThanStartDate,
      candidateLocalStartDate: candidateLocalStartDate,
    };
  } else {
    return {
      isEarlyAccessForbidden: false,
    };
  }
};

/**
 * Return true is the error is a 404 NOT_FOUND error. Otherwise, return false.
 * @param error The error instance from calling the CWB API.
 */
const isNotFound = (error: AxiosError | Error) => {
  return (
    axios.isAxiosError(error) &&
    (error.response?.data as GetUserAliasResponse400)?.code === "NOT_FOUND"
  );
};

/**
 * Numeric supplemental status codes that the lambda emits as part of the
 * `EZO_ERROR` response body. Only the values we actually branch on are
 * referenced by name; other values fall through to the catch-all `Generic`
 * guard.
 */
const SUPPLEMENTAL_CODE = {
  EZO_DISABLED: 1000 satisfies EligibilityStatusCodes,
  INTENTIONAL_DENIAL: 1001 satisfies EligibilityStatusCodes,
  INVALID_COUNTRY_CODE: 1003 satisfies EligibilityStatusCodes,
  INVALID_JOB_LEVEL: 1004 satisfies EligibilityStatusCodes,
  NOT_WITHIN_ELIGIBLE_PERIOD: 1007 satisfies EligibilityStatusCodes,
  MISSING_DATA: 1011 satisfies EligibilityStatusCodes,
} as const;

/**
 * Extract the supplemental status code from an `EZO_ERROR` response body, if
 * any. Returns `undefined` when the error is not an axios error, has no
 * response body, or is not an `EZO_ERROR`. The lambda only attaches
 * `supplementalStatusCode` to `EZO_ERROR` responses (see
 * `CwbApiEzoError.toJSON`), so we narrow on `code === "EZO_ERROR"` to avoid
 * misinterpreting other error shapes.
 */
const getSupplementalStatusCode = (error: unknown): number | undefined => {
  if (!error || !axios.isAxiosError(error)) {
    return undefined;
  }
  const data = error.response?.data as CwbError | undefined;
  if (data?.code !== "EZO_ERROR") {
    return undefined;
  }
  return data.supplementalStatusCode;
};

/**
 * Inputs evaluated by every blocking-error guard. Pre-computed once per render
 * so the guard list can be a pure data structure with cheap predicates.
 */
type BlockingErrorInputs = {
  isUserAliasError: boolean;
  isUserProgressError: boolean;
  isForbidden: boolean;
  isEarlyAccessForbidden: boolean;
  noStartDate: boolean;
  amazonUserAliasError?: AxiosError | Error;
  candidateLocalStartDate?: Dayjs;
  userProgressSupplementalCode?: number;
  aliasSupplementalCode?: number;
};

/**
 * Result of classifying a blocking-error situation. Each field maps directly to
 * one observable side effect (log entry, state update, Snowplow event, dispatch
 * payload) so the effect that consumes a classification doesn't need any
 * branch-specific logic of its own.
 */
type BlockingErrorClassification = {
  kind: BLOCKING_ERROR_TYPE;
  logMessage: string;
  errorToLog?: AxiosError | Error;
  chatContext: ChatSupportContextReason;
  metricAction: Day1EventAction;
  metricValue?: number;
};

/**
 * One entry in the priority-ordered table of blocking-error rules.
 *
 * The list is evaluated top-to-bottom and the first guard whose `match`
 * returns true wins, replicating the previous if/else chain semantics. The
 * ordering matters and should mirror the historical priority — earlier
 * branches win over later ones when multiple inputs are simultaneously true.
 */
type BlockingErrorGuard = {
  name: string;
  match: (inputs: BlockingErrorInputs) => boolean;
  classify: (inputs: BlockingErrorInputs) => BlockingErrorClassification;
};

/**
 * Priority-ordered list of blocking-error guards. The first match wins, so
 * order is significant. New blocking-error states should be inserted at the
 * position that reflects their priority relative to existing entries.
 */
const BLOCKING_ERROR_GUARDS: BlockingErrorGuard[] = [
  {
    name: "BeforeDay1",
    match: (i) => i.isEarlyAccessForbidden,
    classify: (i) => ({
      kind: BLOCKING_ERROR_TYPE.BeforeDay1,
      logMessage:
        "Blocking error - attempting to access the Day One Onboarding Wizard before Day One",
      chatContext: "BLOCKED_BEFORE_DAY_1",
      metricAction: Day1EventAction.NotDay1,
      metricValue: i.candidateLocalStartDate
        ? parseInt(i.candidateLocalStartDate.format("YYYYMMDD"))
        : undefined,
    }),
  },
  {
    name: "NoStartDate",
    match: (i) => i.noStartDate,
    classify: () => ({
      kind: BLOCKING_ERROR_TYPE.Generic,
      logMessage: "Blocking error - new hire has no start date",
      chatContext: "BLOCKED_GENERIC",
      metricAction: Day1EventAction.NoStartDate,
    }),
  },
  // ---------------------------------------------------------------------
  // Per-cause guards driven by the lambda's `supplementalStatusCode`.
  //
  // These run BEFORE the broader `ProgressApiFailed`/`ForbiddenByStatus`
  // guards so a specific eligibility-status code wins over the generic
  // "progress API errored" branch. They also run before `AliasNotFound`
  // because both API responses can carry the same supplemental code when
  // the eligibility gate fires (NHE-5600).
  //
  // Each guard checks both the alias and progress error responses since
  // the eligibility check runs on both endpoints and both will return the
  // same `EZO_ERROR(supplementalCode)` for the same user.
  // ---------------------------------------------------------------------
  {
    name: "EzoDisabled",
    match: (i) => hasSupplementalCode(i, SUPPLEMENTAL_CODE.EZO_DISABLED),
    classify: () => ({
      kind: BLOCKING_ERROR_TYPE.EzoDisabled,
      logMessage:
        "Blocking error - EZO is administratively disabled for this user",
      chatContext: "BLOCKED_EZO_DISABLED",
      metricAction: Day1EventAction.GenericError,
    }),
  },
  {
    name: "IntentionalDenial",
    match: (i) => hasSupplementalCode(i, SUPPLEMENTAL_CODE.INTENTIONAL_DENIAL),
    classify: () => ({
      kind: BLOCKING_ERROR_TYPE.IntentionalDenial,
      logMessage:
        "Blocking error - new hire has been intentionally excluded from EZO",
      chatContext: "BLOCKED_INTENTIONAL_DENIAL",
      metricAction: Day1EventAction.GenericError,
    }),
  },
  {
    name: "InvalidCountryCode",
    match: (i) =>
      hasSupplementalCode(i, SUPPLEMENTAL_CODE.INVALID_COUNTRY_CODE),
    classify: () => ({
      kind: BLOCKING_ERROR_TYPE.InvalidCountryCode,
      logMessage:
        "Blocking error - new hire's hiring country is not eligible for EZO",
      chatContext: "BLOCKED_INVALID_COUNTRY",
      metricAction: Day1EventAction.GenericError,
    }),
  },
  {
    name: "InvalidJobLevel",
    match: (i) => hasSupplementalCode(i, SUPPLEMENTAL_CODE.INVALID_JOB_LEVEL),
    classify: () => ({
      kind: BLOCKING_ERROR_TYPE.InvalidJobLevel,
      logMessage:
        "Blocking error - new hire's job level/family is not eligible for EZO",
      chatContext: "BLOCKED_INVALID_JOB_LEVEL",
      metricAction: Day1EventAction.GenericError,
    }),
  },
  {
    name: "NotWithinEligiblePeriod",
    match: (i) =>
      hasSupplementalCode(i, SUPPLEMENTAL_CODE.NOT_WITHIN_ELIGIBLE_PERIOD),
    classify: () => ({
      kind: BLOCKING_ERROR_TYPE.NotDay1,
      logMessage:
        "Blocking error - new hire is past their EZO eligibility window",
      chatContext: "BLOCKED_NOT_DAY_1",
      metricAction: Day1EventAction.NotDay1,
    }),
  },
  {
    name: "MissingData",
    match: (i) => hasSupplementalCode(i, SUPPLEMENTAL_CODE.MISSING_DATA),
    classify: () => ({
      kind: BLOCKING_ERROR_TYPE.MissingData,
      logMessage: "Blocking error - new hire's record is missing required data",
      chatContext: "BLOCKED_MISSING_DATA",
      metricAction: Day1EventAction.AccountIssue,
    }),
  },
  // ---------------------------------------------------------------------
  // Broader fallback guards. These fire when the lambda did not attach a
  // recognized supplemental status code (e.g. legacy `FORBIDDEN_ERROR`,
  // network failures, or codes we don't yet branch on).These intentionally
  // route to `Generic` rather than `NotDay1`
  // ---------------------------------------------------------------------
  {
    name: "ProgressApiFailed",
    match: (i) => i.isUserProgressError,
    classify: () => ({
      kind: BLOCKING_ERROR_TYPE.Generic,
      logMessage: "Blocking error - getUserProgress API call failed",
      chatContext: "BLOCKED_GENERIC",
      metricAction: Day1EventAction.GenericError,
    }),
  },
  {
    name: "ForbiddenByStatus",
    match: (i) => i.isForbidden,
    classify: () => ({
      kind: BLOCKING_ERROR_TYPE.Generic,
      logMessage:
        "Blocking error - new hire is forbidden from Day One onboarding wizard experience",
      chatContext: "BLOCKED_GENERIC",
      metricAction: Day1EventAction.GenericError,
    }),
  },
  {
    name: "AliasNotFound",
    match: (i) =>
      i.amazonUserAliasError !== undefined &&
      isNotFound(i.amazonUserAliasError),
    classify: (i) => ({
      kind: BLOCKING_ERROR_TYPE.NoAlias,
      logMessage:
        "Blocking error - new hire has an issue with their Amazon user alias",
      errorToLog:
        i.amazonUserAliasError instanceof Error ||
        (i.amazonUserAliasError !== undefined &&
          axios.isAxiosError(i.amazonUserAliasError))
          ? i.amazonUserAliasError
          : undefined,
      chatContext: "BLOCKED_NO_ALIAS",
      metricAction: Day1EventAction.AccountIssue,
    }),
  },
  {
    name: "Generic",
    match: () => true,
    classify: () => ({
      kind: BLOCKING_ERROR_TYPE.Generic,
      logMessage: "Blocking error - generic blocking error",
      chatContext: "BLOCKED_GENERIC",
      metricAction: Day1EventAction.GenericError,
    }),
  },
];

/**
 * Convenience predicate used by the per-cause guards: returns true if either
 * the user-progress or alias error response carries the given supplemental
 * status code. Both endpoints route through the same eligibility gate in the
 * lambda, so a single ineligibility produces the same code on both responses
 * — checking either is sufficient.
 */
const hasSupplementalCode = (
  inputs: BlockingErrorInputs,
  code: number
): boolean =>
  inputs.userProgressSupplementalCode === code ||
  inputs.aliasSupplementalCode === code;

/**
 * Find the first matching blocking-error classification for the given inputs.
 * Returns `undefined` only if no guard matches, which the catch-all entry
 * makes effectively unreachable in practice.
 */
const classifyBlockingError = (
  inputs: BlockingErrorInputs
): BlockingErrorClassification | undefined =>
  BLOCKING_ERROR_GUARDS.find((guard) => guard.match(inputs))?.classify(inputs);

export type UseCheckReqsResult = Pick<
  UseQueryResult<VerificationStatus>,
  "isFetched" | "isError"
> & {
  /** The local NH's local start date*/
  candidateLocalStartDate?: Dayjs;

  /** The blocking error type on the Day1 */
  blockingErrorType?: BLOCKING_ERROR_TYPE;
};

/**
 * Setup Amplify and check all requirements needed for NHs to start their day1 on-boarding.
 * TODO: Check to see if a preference was set for Manual or Automated RIV on PD1 and then set the wizard state accordingly.
 */
export const useCheckReqs = (): UseCheckReqsResult => {
  /** Check if NHs already has an alias assigned to him/her */
  const {
    isError: isUserAliasError,
    isFetched: isUserAliasFetched,
    error: amazonUserAliasError,
  } = useAmazonAlias();

  /**
   * Make sure that NHs are eligible to start their onboarding process. Getting an error from calling the userProgress
   * api should indicate that they are not eligible to start the onboarding process.
   * TODO: This should be expanded to include the preference for RIV path.
   */
  const {
    data: userProgress,
    isError: isUserProgressError,
    isFetched: isUserProgressFetched,
    error: userProgressError,
  } = useUserProgress();

  const isAllFetched = isUserAliasFetched && isUserProgressFetched;
  const [isError, setIsError] = useState(false);
  const [candidateLocalStartDate, setCandidateLocalStartDate] =
    useState<Dayjs>();
  const [blockingErrorType, setBlockingErrorType] =
    useState<BLOCKING_ERROR_TYPE>();

  const { dispatch } = useContext(AppContext);

  /**
   * Check the requirements for continuing the onboarding process and update
   * the loading and error states accordingly.
   */
  useEffect(() => {
    if (!isAllFetched) {
      return;
    }

    const isForbidden = userProgress
      ? isDayOneForbidden(userProgress.verificationStatus)
      : false;
    const { isEarlyAccessForbidden, candidateLocalStartDate } = userProgress
      ? isEarlyAccess(userProgress)
      : { isEarlyAccessForbidden: false, candidateLocalStartDate: undefined };
    setCandidateLocalStartDate(candidateLocalStartDate);

    const noStartDate =
      userProgress !== undefined && !userProgress.candidateStartDate;
    const hasError =
      isUserAliasError ||
      isUserProgressError ||
      isEarlyAccessForbidden ||
      isForbidden ||
      noStartDate;
    setIsError(hasError);

    if (!hasError) {
      return;
    }

    /**
     * Snapshot of every input that contributes to the blocking-error decision.
     * Attached to all blocking-error log entries so we can disambiguate cases
     * where multiple conditions are true at once (e.g. both API calls 403 vs
     * a successful API response that reports forbidden) directly from logs.
     */
    const aliasErrorStatus =
      amazonUserAliasError && axios.isAxiosError(amazonUserAliasError)
        ? amazonUserAliasError.response?.status
        : undefined;
    const aliasErrorCode =
      amazonUserAliasError && axios.isAxiosError(amazonUserAliasError)
        ? (amazonUserAliasError.response?.data as GetUserAliasResponse400)?.code
        : undefined;
    const userProgressSupplementalCode = getSupplementalStatusCode(
      userProgressError ?? undefined
    );
    const aliasSupplementalCode = getSupplementalStatusCode(
      amazonUserAliasError ?? undefined
    );
    const blockingErrorFlags = {
      isUserAliasError,
      isUserProgressError,
      isForbidden,
      isEarlyAccessForbidden,
      noStartDate,
      aliasErrorStatus,
      aliasErrorCode,
      userProgressSupplementalCode,
      aliasSupplementalCode,
    };

    const classification = classifyBlockingError({
      isUserAliasError,
      isUserProgressError,
      isForbidden,
      isEarlyAccessForbidden,
      noStartDate,
      amazonUserAliasError: amazonUserAliasError ?? undefined,
      candidateLocalStartDate,
      userProgressSupplementalCode,
      aliasSupplementalCode,
    });
    if (!classification) {
      return;
    }

    void Logger.warn(classification.logMessage, classification.errorToLog, {
      ...userProgress,
      candidateLocalStartDate,
      blockingErrorFlags,
    });
    setBlockingErrorType(classification.kind);
    trackStructEventOnceAppSession({
      category: Day1EventCategory.BlockingError,
      action: classification.metricAction,
      value: classification.metricValue,
    });
    dispatch({
      type: ActionType.SET_NON_DIRECTED_CHAT_SUPPORT_CONTEXT,
      nonDirectedChatSupportContext: classification.chatContext,
    });
  }, [
    amazonUserAliasError,
    isUserAliasError,
    isUserProgressError,
    userProgress,
    userProgressError,
    isAllFetched,
  ]);

  return {
    isError: isError,
    isFetched: isAllFetched,
    blockingErrorType: blockingErrorType,
    candidateLocalStartDate: candidateLocalStartDate,
  };
};
